// Pure-функции детекта браузеров / WebView'ев по User-Agent. Используется
// для UX-патчей где поведение зависит от среды (например: Telegram WebView
// перехватывает все ссылки и не передаёт Android intent системе, из-за
// чего auth-токен сгорает в WebView и в установленное приложение не доходит
// — см. notes/DEEP_LINK_AUTH_FIX.md).
//
// Все функции принимают UserAgent строкой явно (не window.navigator),
// чтобы тестироваться без браузерных API.

export type Platform = 'android' | 'ios' | 'desktop';

/**
 * Детектит платформу юзера по User-Agent. Нужно для platform-specific
 * UX-патчей:
 * - Android: Intent URLs работают (intent://...#Intent;package=...;end)
 * - iOS: Intent URLs НЕ работают, для выхода из in-app WebView только
 *   инструкции / ссылки в Safari
 * - Desktop: in-app WebViews (типа Telegram Desktop) обычно открывают
 *   default browser сами, но не всегда — показываем инструкцию
 */
export function detectPlatform(userAgent: string | null | undefined): Platform {
  if (!userAgent) return 'desktop';
  const ua = userAgent.toLowerCase();

  // iOS — проверяем первым, т.к. iPad UA на iPadOS 13+ маскируется
  // под Mac но содержит 'mobile' и/или специфичные строки
  if (
    ua.includes('iphone') ||
    ua.includes('ipad') ||
    ua.includes('ipod') ||
    (ua.includes('macintosh') && ua.includes('mobile'))
  ) {
    return 'ios';
  }

  // Android
  if (ua.includes('android')) {
    return 'android';
  }

  return 'desktop';
}

export interface InAppBrowserInfo {
  isInApp: boolean;
  /** Какой именно in-app browser детектирован (для UI-сообщений). */
  source: 'telegram' | 'instagram' | 'vk' | 'facebook' | 'tiktok' | 'whatsapp' | null;
}

/**
 * Детектит in-app browser популярных мессенджеров / соцсетей.
 * Эти браузеры:
 * - перехватывают ссылки и открывают в собственном WebView
 * - не передают Android intent системе
 * - имеют изолированные cookies от системного Chrome
 *
 * Результат TRUE = юзер в in-app браузере → для auth-flow нужно показать
 * CTA «Открыть в установленном приложении» (intent URL) вместо
 * автоматического redirect (который сжёг бы токен в WebView).
 */
export function detectInAppBrowser(userAgent: string | null | undefined): InAppBrowserInfo {
  if (!userAgent) return { isInApp: false, source: null };
  const ua = userAgent.toLowerCase();

  // Telegram WebView (Android: TGAndroid, iOS: Telegram)
  if (ua.includes('telegram') || ua.includes('tgandroid')) {
    return { isInApp: true, source: 'telegram' };
  }
  // Instagram in-app browser
  if (ua.includes('instagram')) {
    return { isInApp: true, source: 'instagram' };
  }
  // ВКонтакте (mobile app + web app)
  if (ua.includes('vkandroid') || ua.includes('vkclient')) {
    return { isInApp: true, source: 'vk' };
  }
  // Facebook in-app
  if (ua.includes('fban') || ua.includes('fbav') || ua.includes('fbios')) {
    return { isInApp: true, source: 'facebook' };
  }
  // TikTok
  if (ua.includes('musical_ly') || ua.includes('tiktok')) {
    return { isInApp: true, source: 'tiktok' };
  }
  // WhatsApp
  if (ua.includes('whatsapp')) {
    return { isInApp: true, source: 'whatsapp' };
  }

  return { isInApp: false, source: null };
}

/**
 * Удобный shortcut: только Telegram WebView (самый частый кейс для auth flow
 * через TG-бота).
 */
export function isTelegramWebView(userAgent: string | null | undefined): boolean {
  return detectInAppBrowser(userAgent).source === 'telegram';
}

/**
 * Строит Android Intent URL для запуска установленного приложения через
 * intent system, минуя in-app WebView браузера.
 *
 * Формат:
 *   intent://favbox.app/auth?token=XXX#Intent;scheme=https;package=app.favbox;end
 *
 * Поведение Android:
 * - Если приложение установлено и intent filter совпадает → откроется в app
 * - Если приложение НЕ установлено → можно опционально указать
 *   S.browser_fallback_url которое откроет fallback в браузере
 *
 * @param targetUrl полный https URL который должен открыться в приложении
 *   (например 'https://favbox.app/auth?token=XXX'). НЕ протокол favbox:// —
 *   intent использует тот же https-URL что и веб, просто wrapped в intent
 *   для forcing Android intent system
 * @param packageName Android package name (например 'app.favbox')
 * @param fallbackUrl опционально: что открыть если app не установлен
 *   (например Play Store install URL)
 */
export function buildAndroidIntentUrl(
  targetUrl: string,
  packageName: string,
  fallbackUrl?: string,
): string {
  // Парсим targetUrl чтобы достать scheme + host + остальное
  // (используем регекс вместо URL constructor — pure-функция без DOM/Node API)
  const match = targetUrl.match(/^(https?):\/\/([^/]+)(\/.*)?$/);
  if (!match) {
    throw new Error(`Invalid targetUrl for intent: ${targetUrl}`);
  }
  const scheme = match[1];
  const hostAndPath = match[2] + (match[3] ?? '');

  let intent = `intent://${hostAndPath}#Intent;scheme=${scheme};package=${packageName}`;
  if (fallbackUrl) {
    intent += `;S.browser_fallback_url=${encodeURIComponent(fallbackUrl)}`;
  }
  intent += ';end';
  return intent;
}

/** event_data для продуктовых событий с разметкой устройства (см. logEvent). */
export interface PlatformEventData {
  /** Платформа по User-Agent: ios / android / desktop. */
  platform: Platform;
  /** Открыто из установленного приложения (PWA/TWA). Признак — кука
   *  `favbox_pwa=1` (ставится клиентом при детекте standalone), поэтому
   *  false может означать и «не установлено», и «кука ещё не поставлена». */
  installed: boolean;
}

/**
 * Собирает разметку устройства для продуктового события: платформа из
 * User-Agent + признак установленного приложения из куки `favbox_pwa`.
 * Pure-функция — оба входа передаются явно, чтобы тестироваться без
 * браузерных/Next API.
 */
export function buildPlatformEventData(
  userAgent: string | null | undefined,
  pwaCookieValue: string | null | undefined,
): PlatformEventData {
  return {
    platform: detectPlatform(userAgent),
    installed: pwaCookieValue === '1',
  };
}

/** Сигналы среды, по которым определяем «запущены ли мы внутри установленного
 *  PWA/TWA». Все читаются на клиенте; функция держит их явными аргументами,
 *  чтобы тестироваться без браузерных API. */
export interface DisplaySignals {
  /** window.matchMedia('(display-mode: standalone)').matches — основной сигнал PWA */
  standalone?: boolean;
  /** window.matchMedia('(display-mode: fullscreen)').matches */
  fullscreen?: boolean;
  /** window.matchMedia('(display-mode: minimal-ui)').matches */
  minimalUi?: boolean;
  /** navigator.standalone === true — iOS Safari «на главный экран» */
  iosStandalone?: boolean;
  /** document.referrer начинается с 'android-app://' — признак запуска из TWA */
  androidAppReferrer?: boolean;
}

/**
 * Определяет, запущена ли страница внутри установленного приложения
 * (PWA через «На главный экран» или TWA из Google Play), а не в обычной
 * вкладке браузера.
 *
 * Зачем: сервер по User-Agent НЕ может отличить установленный PWA в
 * standalone-режиме от обычного Chrome (UA идентичен). Поэтому баннер
 * «Открыть в приложении?» на /auth решается клиентом — если мы уже внутри
 * установленного app, баннер бессмысленен (вход «здесь» = вход в приложении),
 * и его надо пропускать. См. notes/DEEP_LINK_AUTH_FIX.md.
 *
 * TRUE = мы уже внутри установленного приложения.
 */
export function isInstalledAppDisplay(signals: DisplaySignals): boolean {
  return Boolean(
    signals.standalone ||
      signals.fullscreen ||
      signals.minimalUi ||
      signals.iosStandalone ||
      signals.androidAppReferrer,
  );
}
